import { api } from './api'
import type {
  StcRecord,
  StcRecordCreateUpdate,
  StcRecordsListParams,
  StcRecordsPaginatedResponse,
  StcBatchRun,
  StcTypeaheadSuggestion,
} from '../types/stc'
import { getStoredTenantProductType } from '../hooks/useTenantProductType'

const DEFAULT_PAGE_SIZE = 25
const getStcBasePath = () => (getStoredTenantProductType() === 'ppt' ? '/ppt-stc-records' : '/stc-records')

export const stcService = {
  /**
   * Get a single STC record by id (includes formData for pre-fill).
   */
  getById: async (id: string): Promise<StcRecord> => {
    const response = await api.get<StcRecord>(`${getStcBasePath()}/${id}`)
    return response.data
  },

  /**
   * Get presigned URL for the record's signature image. Throws on 404.
   */
  getSignatureUrl: async (id: string): Promise<string> => {
    const response = await api.get<{ url: string; s3Key?: string }>(`${getStcBasePath()}/${id}/signature`)
    return response.data.url
  },

  /**
   * Get signature image as data URL (proxy through API to avoid browser CORS issues with S3 signed URLs).
   */
  getSignatureDataUrl: async (id: string): Promise<string> => {
    const response = await api.get<{ dataUrl: string; s3Key?: string }>(`${getStcBasePath()}/${id}/signature-inline`)
    return response.data.dataUrl
  },

  /**
   * Get presigned URL for the record's saved form PNG. Throws on 404.
   */
  getFormPngUrl: async (id: string): Promise<string> => {
    const response = await api.get<{ url: string; s3Key?: string }>(`${getStcBasePath()}/${id}/form-png`)
    return response.data.url
  },

  /**
   * List STC/MICS records with optional filters and pagination.
   * Returns a paginated response (items, totalCount, totalPages, page, pageSize).
   */
  getRecords: async (params?: StcRecordsListParams): Promise<StcRecordsPaginatedResponse> => {
    const searchParams = new URLSearchParams()
    if (params?.status) searchParams.set('status', params.status)
    if (params?.startDate) searchParams.set('startDate', params.startDate)
    if (params?.endDate) searchParams.set('endDate', params.endDate)
    if (params?.patientId) searchParams.set('patientId', params.patientId)
    if (params?.patientKey) searchParams.set('patientKey', params.patientKey)
    const page = params?.page ?? 1
    const pageSize = params?.pageSize ?? DEFAULT_PAGE_SIZE
    searchParams.set('page', String(page))
    searchParams.set('pageSize', String(pageSize))
    if (params?.sortBy) searchParams.set('sortBy', params.sortBy)
    if (params?.sortDirection) searchParams.set('sortDirection', params.sortDirection)
    const query = searchParams.toString()
    const url = `${getStcBasePath()}?${query}`
    const response = await api.get<StcRecordsPaginatedResponse>(url)
    return response.data
  },

  /**
   * Create or update an STC record.
   */
  createOrUpdate: async (data: StcRecordCreateUpdate): Promise<StcRecord> => {
    const response = await api.post<Omit<StcRecord, 'patientName'>>(getStcBasePath(), data)
    const created = response.data as unknown as StcRecord
    return created
  },

  /**
   * Submit a signed claim (status -> to_pay). Only valid when status is 'signed'.
   */
  submitClaim: async (
    id: string,
    metadata?: { claimNumber?: string; formNumber?: string; claimSummaryUrl?: string }
  ): Promise<StcRecord> => {
    const response = await api.put<StcRecord>(`${getStcBasePath()}/${id}`, {
      status: 'to_pay',
      ...(metadata?.claimNumber ? { claimNumber: metadata.claimNumber } : {}),
      ...(metadata?.formNumber ? { formNumber: metadata.formNumber } : {}),
      ...(metadata?.claimSummaryUrl ? { lastSubmissionClaimSummaryUrl: metadata.claimSummaryUrl } : {}),
    })
    return response.data
  },

  updateStatus: async (
    id: string,
    status: StcRecord['status'],
    options?: { rejectionReason?: string }
  ): Promise<StcRecord> => {
    const payload: Record<string, unknown> = { status }
    if (status === 'rejected' && options?.rejectionReason !== undefined) {
      payload.rejectionReason = options.rejectionReason.trim() || undefined
      payload.rejectedAt = new Date().toISOString()
    }
    const response = await api.put<StcRecord>(`${getStcBasePath()}/${id}`, payload)
    return response.data
  },

  getPrefill: async (entryKey: string): Promise<StcRecord | null> => {
    const base = getStcBasePath()
    if (!base.startsWith('/ppt-stc-records')) {
      return null
    }
    const response = await api.get<{ items: StcRecord[] }>(
      `/ppt-stc-records/prefill?entryKey=${encodeURIComponent(entryKey)}`
    )
    return response.data.items?.[0] ?? null
  },

  getTypeahead: async (
    field: 'patientName' | 'gmsNumber',
    q: string,
    limit = 8
  ): Promise<StcTypeaheadSuggestion[]> => {
    const response = await api.get<{ items: StcTypeaheadSuggestion[] }>(
      `${getStcBasePath()}/typeahead?field=${encodeURIComponent(field)}&q=${encodeURIComponent(q)}&limit=${limit}`
    )
    return response.data.items ?? []
  },

  startBatchRun: async (params: {
    recordIds: string[]
    submitDocNum: string
    submitDocNam: string
  }): Promise<{ batchRunId: string; status: string; totalItems: number; lockedCount: number; skippedCount: number }> => {
    const source = getStoredTenantProductType() === 'ppt' ? 'ppt' : 'pms'
    const response = await api.post<{ batchRunId: string; status: string; totalItems: number; lockedCount: number; skippedCount: number }>(
      '/stc-batches/start',
      {
        source,
        recordIds: params.recordIds,
        submitDocNum: params.submitDocNum,
        submitDocNam: params.submitDocNam,
      }
    )
    return response.data
  },

  getBatchRun: async (id: string): Promise<StcBatchRun> => {
    const response = await api.get<StcBatchRun>(`/stc-batches/${id}`)
    return response.data
  },

  listBatchRuns: async (limit = 10): Promise<StcBatchRun[]> => {
    const response = await api.get<{ items: StcBatchRun[] }>(`/stc-batches?limit=${limit}`)
    return response.data.items ?? []
  },
}
